/**
 * src/components/agent/VoicePickerSheet.jsx — Voice alias picker (Phase 82-05).
 *
 * Bottom sheet that lists the four Voice Mode aliases (Nora / Sofía / Marco /
 * Elías). Tap picks a voice, persists via presentation.controls.setVoice, and
 * closes. 56px touch targets per row.
 */

import { VOICES } from '../../lib/voice/voiceOptions';
import { useT } from '../../lib/i18n/I18nContext';

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  zIndex: 10010,
};

const sheetStyle = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--card)',
  color: 'var(--t1)',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  boxShadow: 'var(--she)',
  zIndex: 10011,
  padding: '12px 0 calc(24px + env(safe-area-inset-bottom))',
  maxHeight: '60vh',
  overflowY: 'auto',
};

const titleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--tm)',
  padding: '4px 20px 12px',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const rowStyle = (active) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 20px',
  minHeight: 56,
  cursor: 'pointer',
  background: active ? 'var(--accent-bg)' : 'transparent',
  color: 'var(--t1)',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  fontSize: 15,
});

const dotStyle = (locale) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: locale === 'en' ? 'var(--info-tint)' : 'var(--accent-bg)',
  color: 'var(--accent)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 13,
});

const nameStyle = { flex: 1, fontWeight: 500 };
const localeStyle = { fontSize: 12, color: 'var(--tm)' };

export default function VoicePickerSheet({ open, currentVoiceId, onSelect, onClose }) {
  const { t } = useT();
  if (!open) return null;
  return (
    <>
      <div
        style={backdropStyle}
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label={t('agentChat.closeVoicePicker')}
        onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') onClose?.(); }}
        data-testid="voice-picker-backdrop"
      />
      <div style={sheetStyle} role="dialog" aria-label="Pick voice" data-testid="voice-picker-sheet">
        <div style={titleStyle}>Voice</div>
        {VOICES.map((v) => {
          const active = v.id === currentVoiceId;
          const initial = v.displayName.charAt(0).toUpperCase();
          return (
            <button
              key={v.id}
              type="button"
              style={rowStyle(active)}
              onClick={() => { onSelect?.(v.id); onClose?.(); }}
              data-testid={`voice-picker-row-${v.id}`}
              aria-pressed={active}
            >
              <span style={dotStyle(v.locale)} aria-hidden="true">{initial}</span>
              <span style={nameStyle}>{v.displayName}</span>
              <span style={localeStyle}>{v.locale.toUpperCase()}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
